import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Avatar, Badge, Button, Dropdown, Empty, Input, List, Modal, Select, Space, Spin, Tag, Tooltip, Typography, message } from 'antd'
import { EditOutlined, ReloadOutlined, SendOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { leadsApi, type Lead, type LeadMessage } from '../services/leadsApi'
import { temperatureColor } from '../constants/leadTemperature'
import { useAuthStore } from '../store/authStore'
import Contacts from './Contacts'
import TgRichEditor from '../components/TgRichEditor'
import TgMessagePreview from '../components/TgMessagePreview'
import FormattedText from '../components/FormattedText'
import { hasTgFormatting, templateToTg, tgToPlain } from '../utils/tgFormat'

const { Text, Title } = Typography

type QueueTab = 'queued' | 'failed' | 'sent'

const TAB_TITLES: Record<QueueTab, string> = {
    queued: 'В очереди',
    failed: 'Ошибки',
    sent: 'Отправлено',
}

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response
        return response?.data?.detail || 'Ошибка запроса'
    }
    return 'Ошибка запроса'
}

// Ошибки, после которых повторять бессмысленно: виноват не аккаунт, а сам
// контакт. Система такие попытки и не повторяет - подписи должны говорить об
// этом прямо, иначе «Попытка 1/3» читается как «сейчас попробует ещё».
const FINAL_ERROR_LABELS: Record<string, string> = {
    invalid_contact_data: 'Такого пользователя нет в Telegram — попытки прекращены',
    repeated_unknown_error: 'Одна и та же ошибка на двух аккаунтах — проблема с лидом, попытки прекращены',
    phone_not_found: 'Ника нет или он не сработал, по телефону не найден — попытки прекращены',
    phone_error: 'Ника нет или он не сработал, отправка по телефону не удалась — попытки прекращены',
    blocked_us: 'Лид заблокировал наш аккаунт — попытки прекращены',
    privacy_restricted: 'Настройки приватности лида не дают написать — попытки прекращены',
    paid_message_required: 'Лид принимает только платные сообщения — попытки прекращены',
}

function finalErrorLabel(lead: Lead): string | null {
    if (lead.dispatch_status !== 'failed') return null
    return FINAL_ERROR_LABELS[lead.dispatch_error_type || ''] || 'Попытки прекращены'
}

// Построчный статус в очереди: «Ошибка отправки» означает, что попытки ещё есть
// и система повторит сама; «Неотправлено» - решение за человеком.
function queueStatusLabel(lead: Lead): { text: string; color: string } {
    if (lead.dispatch_status === 'sent' && lead.dispatch_error_type === 'sent_by_phone') return { text: 'Написали по телефону', color: 'green' }
    if (lead.dispatch_status === 'sent') return { text: 'Отправлено', color: 'green' }
    if (lead.dispatch_status === 'failed') return { text: 'Неотправлено', color: 'red' }
    if (lead.dispatch_error) return { text: 'Ошибка отправки', color: 'orange' }
    return { text: 'В очереди', color: 'default' }
}

// Чем лид называется, когда ник и телефон скрыты: идентификатор его карточки
// в CRM Wiseway, а у заведённых вручную - наш номер.
function leadRef(lead: Pick<Lead, 'id' | 'crm_ww_id'>): string {
    const crmId = (lead.crm_ww_id || '').trim()
    return crmId ? `ID CRM WW ${crmId}` : `Лид #${lead.id}`
}

function leadName(lead: Lead): string {
    return lead.title || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.telegram_nickname || leadRef(lead)
}

function leadUsername(lead: Lead): string {
    if (lead.contacts_hidden) return leadRef(lead)
    const value = lead.telegram_username || lead.telegram_nickname || ''
    return value.startsWith('@') ? value : value ? `@${value}` : 'Telegram не указан'
}

export default function BroadcastQueue() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const [activeTab, setActiveTab] = useState<QueueTab>('queued')
    const [leads, setLeads] = useState<Lead[]>([])
    const [counts, setCounts] = useState<Record<string, number>>({})
    const [maxAttempts, setMaxAttempts] = useState(3)
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
    const [messagesList, setMessagesList] = useState<LeadMessage[]>([])
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [resendingLeadId, setResendingLeadId] = useState<number | null>(null)
    const [dismissingLeadId, setDismissingLeadId] = useState<number | null>(null)
    const [cardLeadId, setCardLeadId] = useState<number | null>(null)
    // Меню по правой кнопке на лиде во вкладке «В очереди».
    const [menuLead, setMenuLead] = useState<Lead | null>(null)
    const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)

    const [manualModalOpen, setManualModalOpen] = useState(false)
    const [manualText, setManualText] = useState('')
    const [manualSending, setManualSending] = useState(false)


    const loadCounts = useCallback(async () => {
        try {
            const response = await leadsApi.getBroadcastQueueCounts()
            setCounts(response.data || {})
            if (response.data?.max_send_attempts) setMaxAttempts(response.data.max_send_attempts)
        } catch {
            setCounts({})
        }
    }, [])

    const loadQueue = useCallback(async () => {
        setLoading(true)
        try {
            const response = await leadsApi.getBroadcastQueue({
                dispatch_status: activeTab,
                search: search.trim() || undefined,
                limit: 200,
            })
            setLeads(response.data)
            setSelectedLead((current) => {
                if (current && response.data.some((lead) => lead.id === current.id)) {
                    return response.data.find((lead) => lead.id === current.id) || null
                }
                return response.data[0] || null
            })
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [activeTab, search])

    useEffect(() => {
        loadQueue()
        loadCounts()
    }, [loadQueue, loadCounts])

    // Раздел живой: очередь разбирается фоновым диспетчером, поэтому обновляем
    // список сами, не дожидаясь ручного нажатия на «Обновить».
    useEffect(() => {
        const interval = window.setInterval(() => {
            loadQueue()
            loadCounts()
        }, 30000)
        return () => window.clearInterval(interval)
    }, [loadQueue, loadCounts])

    const loadMessages = useCallback(async () => {
        if (!selectedLead || !selectedLead.chat_id) {
            setMessagesList([])
            return
        }
        setLoadingMessages(true)
        try {
            const response = await leadsApi.getMessages(selectedLead.id, { limit: 100 })
            setMessagesList(response.data)
        } catch {
            setMessagesList([])
        } finally {
            setLoadingMessages(false)
        }
    }, [selectedLead])

    useEffect(() => {
        loadMessages()
    }, [loadMessages])

    // Карточка открыта и выбрали другого лида в списке - показываем его карточку,
    // а не оставляем висеть прежнюю. Ушли со всех лидов - закрываем.
    const selectedLeadId = selectedLead?.id ?? null
    useEffect(() => {
        setCardLeadId((current) => (current === null ? null : selectedLeadId))
    }, [selectedLeadId])



    const resendFirstMessage = async (lead: Lead) => {
        setResendingLeadId(lead.id)
        try {
            await leadsApi.resendFirstMessage(lead.id)
            // Отправка идёт через общую очередь с её паузами, а не сразу.
            message.success('Лид поставлен в очередь на отправку')
            await Promise.all([loadQueue(), loadCounts()])
        } catch (error) {
            message.error(extractError(error))
            await Promise.all([loadQueue(), loadCounts()])
        } finally {
            setResendingLeadId(null)
        }
    }

    const dismissError = async (lead: Lead) => {
        setDismissingLeadId(lead.id)
        try {
            await leadsApi.dismissError(lead.id)
            await Promise.all([loadQueue(), loadCounts()])
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setDismissingLeadId(null)
        }
    }

    // Отмена только снимает лида с автоматической отправки: ничего не уходит
    // ни в Telegram, ни в CRM. Написать ему потом можно вручную.
    const cancelDispatch = (lead: Lead) => {
        Modal.confirm({
            title: 'Отменить отправку?',
            content: `Лид «${leadName(lead)}» уйдёт из очереди, первое сообщение ему автоматически не отправится. Написать ему можно будет вручную.`,
            okText: 'Отменить отправку',
            okButtonProps: { danger: true },
            cancelText: 'Назад',
            onOk: async () => {
                try {
                    await leadsApi.cancelDispatch(lead.id)
                    message.success('Отправка отменена')
                } catch (error) {
                    message.error(extractError(error))
                }
                await Promise.all([loadQueue(), loadCounts()])
            },
        })
    }

    const openManualSend = async () => {
        if (!selectedLead) return
        try {
            const response = await leadsApi.getFirstMessageTemplate()
            // В редакторе - разметка Telegram; старый шаблон - простой текст.
            setManualText(templateToTg(response.data.content || '', response.data.is_formatted))
        } catch {
            setManualText('')
        }
        setManualModalOpen(true)
    }

    const sendManual = async () => {
        const plain = tgToPlain(manualText).trim()
        if (!selectedLead || !plain) return
        setManualSending(true)
        try {
            await leadsApi.sendMessage(
                selectedLead.id, plain, true, null, hasTgFormatting(manualText) ? manualText : null,
            )
            message.success('Сообщение отправлено')
            setManualModalOpen(false)
            setManualText('')
            await Promise.all([loadQueue(), loadCounts(), loadMessages()])
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setManualSending(false)
        }
    }

    // Во вкладке «Ошибки» в списке только имя и Telegram: подробности и так
    // справа, а длинные строки с причиной растягивали каждую запись.
    const renderFailedItem = (lead: Lead) => (
        <List.Item
            className="lead-chat-list-item"
            onClick={() => setSelectedLead(lead)}
            style={{
                cursor: 'pointer',
                padding: '10px 14px',
                borderRadius: 8,
                borderLeft: `4px solid ${temperatureColor(lead.temperature)}`,
                background: selectedLead?.id === lead.id ? 'var(--lead-selected-bg)' : undefined,
            }}
        >
            <div style={{ minWidth: 0, width: '100%' }}>
                <Text strong ellipsis style={{ display: 'block' }}>{leadName(lead)}</Text>
                <Text type="secondary" ellipsis style={{ display: 'block' }}>{leadUsername(lead)}</Text>
            </div>
        </List.Item>
    )

    const renderLeadItem = (lead: Lead) => (
        <List.Item
            className="lead-chat-list-item"
            onClick={() => setSelectedLead(lead)}
            onContextMenu={(event) => {
                if (activeTab !== 'queued') return
                event.preventDefault()
                setMenuLead(lead)
                setMenuPos({ x: event.clientX, y: event.clientY })
            }}
            style={{
                cursor: 'pointer',
                padding: '12px 14px',
                borderRadius: 8,
                borderLeft: `4px solid ${temperatureColor(lead.temperature)}`,
                background: selectedLead?.id === lead.id ? 'var(--lead-selected-bg)' : undefined,
            }}
        >
            <List.Item.Meta
                avatar={<Badge dot={Boolean(lead.unread_count)} color="red"><Avatar src={lead.telegram_avatar_url || undefined} icon={<UserOutlined />} /></Badge>}
                title={
                    <Space size={6}>
                        <Text strong>{leadName(lead)}</Text>
                        <Tag color="geekblue">#{lead.id}</Tag>
                    </Space>
                }
                description={
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text type="secondary">{leadUsername(lead)}</Text>
                        <Text type="secondary">{lead.geo || 'Гео не указано'}</Text>
                        <Space size={6} wrap>
                            <Tag color={queueStatusLabel(lead).color}>{queueStatusLabel(lead).text}</Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {finalErrorLabel(lead) || `Попытка ${lead.dispatch_attempts || 0}/${maxAttempts}`}
                            </Text>
                        </Space>
                        {lead.dispatch_error && (
                            <Text type={lead.dispatch_status === 'failed' ? 'danger' : 'warning'} style={{ fontSize: 12 }}>
                                {lead.dispatch_error}
                            </Text>
                        )}
                    </Space>
                }
            />
        </List.Item>
    )

    return (
        <div className="lead-workspace lead-chats-page" style={{ display: 'grid', gridTemplateColumns: '336px minmax(0, 1fr)', height: 'calc(100vh - 64px)', gap: 16 }}>
            <aside className="lead-chat-sidebar" style={{ borderRight: '1px solid var(--lead-border)', padding: '0 22px 0 18px', minWidth: 0 }}>
                <Space direction="vertical" size={12} style={{ width: '100%', height: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Title level={4} style={{ margin: 0 }}>Очередь рассылок</Title>
                        <Space size={6}>
                            <Tooltip title="Настройки рассылки перенесены в раздел «Настройки»">
                                <Button icon={<SettingOutlined />} onClick={() => navigate('/settings')} />
                            </Tooltip>
                            <Tooltip title="Обновить">
                                <Button icon={<ReloadOutlined />} onClick={() => { loadQueue(); loadCounts() }} loading={loading} />
                            </Tooltip>
                        </Space>
                    </Space>

                    <Select
                        value={activeTab}
                        style={{ width: '100%' }}
                        onChange={(value: QueueTab) => { setActiveTab(value); setSelectedLead(null) }}
                        options={(Object.keys(TAB_TITLES) as QueueTab[]).map((tab) => ({
                            value: tab,
                            label: `${TAB_TITLES[tab]} (${counts[tab] ?? 0})`,
                        }))}
                    />

                    <Input.Search
                        placeholder={user?.contacts_hidden ? "Поиск по имени и ID CRM WW" : "Поиск по имени, нику, телефону"}
                        allowClear
                        onSearch={(value) => setSearch(value)}
                    />

                    <div className="lead-chat-list-scroll">
                        <Spin spinning={loading}>
                            {menuLead && menuPos && (
                                <Dropdown
                                    open
                                    trigger={['contextMenu']}
                                    onOpenChange={(open) => {
                                        if (!open) {
                                            setMenuPos(null)
                                            setMenuLead(null)
                                        }
                                    }}
                                    menu={{
                                        items: [{ key: 'cancel', label: 'Отмена отправки', danger: true }],
                                        onClick: () => {
                                            const lead = menuLead
                                            setMenuPos(null)
                                            setMenuLead(null)
                                            cancelDispatch(lead)
                                        },
                                    }}
                                >
                                    <div style={{ position: 'fixed', left: menuPos.x, top: menuPos.y, width: 1, height: 1 }} />
                                </Dropdown>
                            )}
                            <List
                                dataSource={leads}
                                locale={{ emptyText: <Empty description="Рассылок в этой вкладке нет" /> }}
                                renderItem={activeTab === 'failed' ? renderFailedItem : renderLeadItem}
                            />
                        </Spin>
                    </div>
                </Space>
            </aside>

            <main className="lead-chat-main" style={{ minWidth: 0, paddingLeft: 22, paddingRight: 22, overflowY: 'auto' }}>
                {selectedLead ? (
                    <Space direction="vertical" size={14} style={{ width: '100%' }}>
                        <div className="lead-chat-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Space align="center" size={12} wrap style={{ minWidth: 0 }}>
                                <Avatar size={48} src={selectedLead.telegram_avatar_url || undefined} icon={<UserOutlined />} />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>{leadName(selectedLead)}</Title>
                                    <Space size={8} wrap>
                                        <Tag color="geekblue">ID лида: {selectedLead.id}</Tag>
                                        {selectedLead.geo && <Tag>{selectedLead.geo}</Tag>}
                                        <Text type="secondary">{leadUsername(selectedLead)}</Text>
                                        {selectedLead.dispatch_attempt_at && (
                                            <Text type="secondary">Попытка: {dayjs(selectedLead.dispatch_attempt_at).format('DD.MM.YYYY HH:mm')}</Text>
                                        )}
                                    </Space>
                                </div>
                            </Space>
                            {/* Та же кнопка и на том же месте, что в шапке «Чатов». */}
                            <div className="chat-detail-actions">
                                <button
                                    type="button"
                                    className="chat-btn-ghost"
                                    onClick={() => setCardLeadId(cardLeadId === selectedLead.id ? null : selectedLead.id)}
                                >
                                    {cardLeadId === selectedLead.id ? 'Скрыть карточку' : 'Показать карточку'}
                                </button>
                            </div>
                        </div>

                        {selectedLead.dispatch_error && (
                            <Alert
                                type={activeTab === 'failed' ? 'error' : 'warning'}
                                showIcon
                                message="Причина неудачной отправки"
                                description={
                                    <Space direction="vertical" size={8}>
                                        <Text>{selectedLead.dispatch_error}</Text>
                                        <Text type="secondary">
                                            {finalErrorLabel(selectedLead)
                                                || `Попытка ${selectedLead.dispatch_attempts || 0} из ${maxAttempts}`}
                                            {selectedLead.dispatch_next_retry_at
                                                ? ` · следующая попытка ${dayjs(selectedLead.dispatch_next_retry_at).format('DD.MM HH:mm')}`
                                                : selectedLead.dispatch_status === 'failed' ? ' · попытки исчерпаны' : ''}
                                        </Text>
                                        <Space size={8} wrap>
                                            <Button
                                                icon={<SendOutlined />}
                                                onClick={() => resendFirstMessage(selectedLead)}
                                                loading={resendingLeadId === selectedLead.id}
                                            >
                                                Переотправить
                                            </Button>
                                            <Button icon={<EditOutlined />} onClick={openManualSend}>
                                                Изменить текст и отправить
                                            </Button>
                                            {/* Причину прочитали - её можно убрать. Отправку это
                                                не запускает и попытки не сбрасывает. */}
                                            <Button
                                                onClick={() => dismissError(selectedLead)}
                                                loading={dismissingLeadId === selectedLead.id}
                                            >
                                                Скрыть причину
                                            </Button>
                                        </Space>
                                    </Space>
                                }
                            />
                        )}

                        {selectedLead.chat_id ? (
                            <Spin spinning={loadingMessages}>
                                <div className="lead-chat-messages" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                                    {messagesList.length === 0 ? (
                                        <Empty description="История сообщений пока пустая" />
                                    ) : (
                                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                            {messagesList.map((item) => (
                                                <div key={item.id} className={`lead-message-row ${item.outgoing ? 'is-outgoing' : 'is-incoming'}`}>
                                                    <div className="lead-message-bubble">
                                                        {item.text && (
                                                            <div>{item.text_html ? <FormattedText html={item.text_html} /> : item.text}</div>
                                                        )}
                                                        <div className="lead-message-meta">
                                                            <span>{dayjs(item.date).format('DD.MM HH:mm')}</span>
                                                            {item.outgoing && <span>{item.is_read ? 'Прочитано' : 'Отправлено'}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </Space>
                                    )}
                                </div>
                            </Spin>
                        ) : (
                            <Empty description={activeTab === 'queued' ? 'Лид ожидает первой отправки' : 'Чат ещё не создан'} />
                        )}
                    </Space>
                ) : (
                    <Empty description="Выберите рассылку из списка" style={{ marginTop: 80 }} />
                )}
            </main>

            <Modal
                title="Отправить вручную"
                open={manualModalOpen}
                onCancel={() => setManualModalOpen(false)}
                onOk={sendManual}
                okText="Отправить"
                cancelText="Отмена"
                confirmLoading={manualSending}
                width={640}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Текст меняется только для этого лида"
                    description="Общий шаблон остаётся прежним — его можно отредактировать кнопкой «Шаблон первого сообщения»."
                />
                <TgRichEditor value={manualText} onChange={setManualText} minRows={6} maxRows={14} />
                <TgMessagePreview value={manualText} withSamples={false} />
            </Modal>

            {/* Та же карточка, что в «Чатах» и «Карточках лидов», во встроенном режиме. */}
            {cardLeadId !== null && (
                <Contacts
                    key={cardLeadId}
                    embeddedLeadId={cardLeadId}
                    onEmbeddedClose={() => {
                        setCardLeadId(null)
                        // В карточке могли поправить ник или имя - обновим список.
                        loadQueue()
                    }}
                />
            )}


        </div>
    )
}
